import api from './api';

export interface Banco {
    nroBanco: number;
    nombreBanco: string;
    tipoDeCuenta?: string;
    nroDeCuenta?: string;
    cbu?: string;
    titular?: string;
    sucursal?: string;
    nroCtaCble?: number;
    [key: string]: any; // Para campos adicionales que puedan venir del backend
}

export interface SucursalBanco {
    nroBanco: number;
    nroSucursal: number;
    sucursal: string;
    calle?: string | null;
    codigoCiudad?: number | null;
}

export type SucursalBancoUpdate = Omit<SucursalBanco, 'nroBanco' | 'nroSucursal'>;

/** SucursalBancoCreateDto / SucursalBancoUpdateDto: sucursal y calle máx. 40. */
export const SUCURSAL_BANCO_NOMBRE_MAX = 40;
export const SUCURSAL_BANCO_CALLE_MAX = 40;

const SUCURSALES_BANCO_URL = '/api/Tesoreria/sucursales-banco';

export interface BancoSearchParams {
    searchTerm?: string;
    pageNumber: number;
    pageSize: number;
}

export interface BancoPagedResult {
    items: Banco[];
    totalCount: number;
    pageNumber: number;
    pageSize: number;
    totalPages: number;
}

const bancoService = {
    getBancos: async (params: BancoSearchParams): Promise<BancoPagedResult> => {
        const response = await api.get<BancoPagedResult>('/api/Banco', { 
            params: {
                SearchTerm: params.searchTerm,
                PageNumber: params.pageNumber,
                PageSize: params.pageSize
            }
        });
        return response.data;
    },

    getBanco: async (nroBanco: number): Promise<Banco> => {
        const response = await api.get<Banco>(`/api/Banco/${nroBanco}`);
        return response.data;
    },

    createBanco: async (banco: Banco): Promise<Banco> => {
        const response = await api.post<Banco>('/api/Banco', banco);
        return response.data;
    },

    updateBanco: async (nroBanco: number, banco: Banco): Promise<void> => {
        await api.put(`/api/Banco/${nroBanco}`, banco);
    },

    deleteBanco: async (nroBanco: number): Promise<void> => {
        await api.delete(`/api/Banco/${nroBanco}`);
    },

    getSucursales: async (nroBanco: number): Promise<SucursalBanco[]> => {
        const response = await api.get<SucursalBanco[]>(SUCURSALES_BANCO_URL, { params: { nroBanco } });
        const items = Array.isArray(response.data) ? response.data : [];
        return items.filter((s) => Number(s.nroBanco) === Number(nroBanco));
    },

    createSucursal: async (sucursal: SucursalBanco): Promise<SucursalBanco> => {
        const response = await api.post<SucursalBanco>(SUCURSALES_BANCO_URL, sucursal);
        return response.data;
    },

    updateSucursal: async (nroBanco: number, nroSucursal: number, sucursal: SucursalBancoUpdate): Promise<void> => {
        await api.put(`${SUCURSALES_BANCO_URL}/${nroBanco}/${nroSucursal}`, sucursal);
    },

    deleteSucursal: async (nroBanco: number, nroSucursal: number): Promise<void> => {
        await api.delete(`${SUCURSALES_BANCO_URL}/${nroBanco}/${nroSucursal}`);
    },

    descargarPlantillaSucursales: async (): Promise<Blob> => {
        const { data } = await api.get(`${SUCURSALES_BANCO_URL}/importacion/plantilla`, {
            responseType: 'blob',
        });
        return data;
    },

    importarSucursales: async (archivo: File, dryRun: boolean): Promise<SucursalBancoImportResult> => {
        const form = new FormData();
        form.append('archivo', archivo);
        form.append('dryRun', String(dryRun));
        const { data } = await api.post<SucursalBancoImportResult>(
            `${SUCURSALES_BANCO_URL}/importacion`,
            form,
            { headers: { 'Content-Type': 'multipart/form-data' } },
        );
        return data;
    },
};

export interface SucursalBancoImportFila {
    fila: number;
    columna: string;
    mensaje: string;
}

export interface SucursalBancoImportResult {
    dryRun: boolean;
    importado: boolean;
    filasLeidas: number;
    creadas: number;
    actualizadas: number;
    omitidas: number;
    conError: number;
    erroresNoListados: number;
    errores: SucursalBancoImportFila[];
    advertencias: SucursalBancoImportFila[];
}

export default bancoService;


